// Similar Requests Service - Backend API Integration
// This service handles communication with the backend for similar requests

import { apiKeyService } from './apiKeyService';

export interface SimilarRequest {
  id: string;
  gid: string;
  title: string;
  status?: 'pending' | 'in-progress' | 'completed';
  date: string;
  created_at: string;
  program: string;
  team?: string;
  description?: string;
  link?: string; // Link to the request (e.g., Asana URL)
  permalink_url?: string; // Asana permalink URL
  target_year?: string; // Target year for the request
  priority?: 'low' | 'medium' | 'high';
  tags?: string[];
  similarity_score?: number;
}

export interface SimilarRequestsResponse {
  similar_requests: SimilarRequest[];
  count: number;
  query_info: {
    program_filter: string;
  };
}

export interface SimilarRequestsQuery {
  context: string; // This will be the problem_statement
  team?: string;
  program_name?: string;
}

class SimilarRequestsService {
  
  private cachedData: SimilarRequestsResponse | null = null;

  /**
   * Get cached similar requests data
   */
  getCachedData(): SimilarRequestsResponse | null {
    return this.cachedData;
  }

  /**
   * Clear cached data
   */
  clearCache(): void {
    this.cachedData = null;
  }

  /**
   * Fetch similar requests based on problem statement, team, and program
   * @param problemStatement - The problem statement from the conversation
   * @param team - The assigned team (optional)
   * @param program - The program name (optional)
   * @param projectId - The Asana project ID (required)
   */
  async fetchSimilarRequests(problemStatement: string, team?: string, program?: string, projectId?: string): Promise<SimilarRequestsResponse> {
    // Validate projectId is provided
    if (!projectId) {
      throw new Error('Missing projectId parameter - Asana project ID is required');
    }
    
    const query = {
      context: problemStatement,
      team: team,
      program_name: program,
      projectId: projectId
    };

    try {
      // Get API key headers from the centralized service
      const headers = await apiKeyService.getApiKeyHeaders();
      
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/similar-requests`, {
        method: 'POST',
        headers,
        body: JSON.stringify(query)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Cache the result for the sidebar to use
      this.cachedData = data;
      
      return data;
    } catch (error) {
      console.error('❌ SIMILAR REQUESTS SERVICE ERROR: Failed to fetch similar requests:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const similarRequestsService = new SimilarRequestsService();

// Export types and service class
export default SimilarRequestsService;
